import { useMemo, useRef, useState } from 'react';
import { Box, Typography, Stack } from '@mui/material';
import { getUserSettings, getWindow } from '@surfline/web-common';

import ErrorBoundary from 'components/ErrorBoundary';
import ErrorMessage from 'components/ErrorMessage';
import ForecastTableV2 from 'components/ForecastTableV2';
import { useDeviceSize } from 'hooks/useDeviceSize';
import useTableViewState from 'hooks/useTableViewState';
import { getSpotReportAssociated } from 'selectors/spot';
import { getSubregionOverviewData } from 'selectors/subregion';
import { useUserPermissionStatus } from 'selectors/user';
import { useAppSelector } from 'stores/hooks';
import type { Units } from 'types/units';

import { SL_WEB_FREEMIUM_HOURLY_DATA } from 'common/treatments';
import { useForecastDaysAllowed } from 'utils/forecastDaysAllowed';
import { useTreatments } from 'utils/treatments';
import GraphContainer from './GraphContainer';
import GraphHeader from './GraphHeader/GraphHeader';
import GraphDaySelectorContainer from './GraphDaySelectorContainer';
import useDaysAndRanges from './hooks/useDaysAndRanges';
import useFetchGraphData from './hooks/useFetchGraphData';
import useSpotPageScroll from './hooks/useSpotPageScroll';
import useUpdateHistoricForecastURL from './hooks/useUpdateHistoricForecastURL';
import { useGlobalSwellSpectraGraphOpen } from './hooks/useStoredSwellSpectraGraphToggle';
import { formatLastModelRunTimestamp } from './utils';

import styles from './ForecastDataVisualizer.module.scss';

interface Props {
  spotId: string;
  spotName: string;
  subregionId: string;
  hasLiveWind: boolean;
  consistencyEnabled: boolean;
  spectraDataAvailable: boolean;
  supportsInsights: boolean;
  utcOffset: number;
  initialStartDayIndex?: number;
  units: Units;
  referrer?: string;
}

const ModelRunTimestamp = ({
  abbrTimezone,
  dateFormat,
  timestamp,
  utcOffset,
}: {
  abbrTimezone: string;
  dateFormat: string | undefined;
  timestamp: number;
  utcOffset: number;
}) => (
  <Stack direction="column" position="relative">
    <Typography
      variant="footnote"
      color="text.secondary"
      className={styles.modelRunCompleted}
      component="span"
      data-testid="forecast-last-model-run-timestamp"
    >
      {formatLastModelRunTimestamp(abbrTimezone, dateFormat, timestamp, utcOffset)}
    </Typography>
    <Typography
      variant="footnote"
      color="text.secondary"
      className={styles.modelRunCompleted}
      component="span"
    >
      Swell: LOTUS
    </Typography>
  </Stack>
);

const ForecastDataVisualizerContent: React.FC<Props> = ({
  spotId,
  spotName,
  subregionId,
  hasLiveWind,
  consistencyEnabled,
  spectraDataAvailable,
  supportsInsights,
  utcOffset,
  initialStartDayIndex,
  units,
}) => {
  const subregionOverview = useAppSelector(getSubregionOverviewData);
  const treatments = useTreatments();
  const { hasCoreForecastPermissions, hasHistoricForecastPermissions } = useUserPermissionStatus();
  const [isSwellSpectraOpen] = useGlobalSwellSpectraGraphOpen();
  const hasHourlyDataAccess =
    hasCoreForecastPermissions || treatments?.[SL_WEB_FREEMIUM_HOURLY_DATA] === 'on';
  const graphContainerRef = useRef<HTMLDivElement | null>(null);

  // Use the new table view state hook
  const { isTableViewMode, toggleTableView } = useTableViewState({
    spotId,
    spotName,
    subregionId,
    subregionName: subregionOverview?.name,
  });

  const { abbrTimezone } = useAppSelector(getSpotReportAssociated);
  const settings = useAppSelector(getUserSettings);
  const dateFormat = settings?.date?.format;

  const { forecastDaysAllowed } = useForecastDaysAllowed({
    type: 'spot',
    isEntitled: hasCoreForecastPermissions,
  });

  const {
    handleSetStartDate,
    isHistoricView,
    showHistoricForecast,
    startDate,
    toggleHistoricView,
  } = useUpdateHistoricForecastURL({
    isPermitted: hasHistoricForecastPermissions,
    segmentSpotDetails: { spotId, spotName, subregionId, subregionName: subregionOverview?.name },
  });

  useSpotPageScroll();

  const graphData = useFetchGraphData({
    spotId,
    subregionId,
    isPremium: hasCoreForecastPermissions,
    hasHourlyDataAccess,
    numDaysToFetch: forecastDaysAllowed,
    maxDaysToFetch: forecastDaysAllowed,
    hasLiveWind,
    consistencyEnabled,
    spectraGraphOpen: isSwellSpectraOpen,
    spectraDataAvailable,
    supportsInsights,
    units,
    isHistoricView,
    startDate: hasCoreForecastPermissions ? startDate : undefined,
  });

  const isPaywallActive = !hasCoreForecastPermissions;

  const {
    consistency,
    daySummary,
    didCoreFetchesError,
    liveWind,
    ratings,
    sunlightTimes,
    surf,
    swells,
    swellSpectra,
    tide,
    weather,
    weatherRaw,
    waveEnergy,
    wind,
  } = graphData;

  const trackingDetails = useMemo(
    () => ({
      spotName,
      spotId,
      subregionName: subregionOverview?.name,
      subregionId,
    }),
    [spotId, spotName, subregionId, subregionOverview?.name],
  );

  const [selectedStartDay, setSelectedStartDay] = useState(0);
  const [previousStartDay, setPreviousStartDay] = useState(0);
  const [swipeLeftCount, setSwipeLeftCount] = useState(0);
  const [swipeRightCount, setSwipeRightCount] = useState(0);
  const device = useDeviceSize();

  const { daysToDisplay, daysPerRange } = useDaysAndRanges({
    deviceWidth: device.width,
    startDay: selectedStartDay,
  });
  const firstDayIndex = useMemo(() => daysToDisplay[0], [daysToDisplay]);
  const lastDayIndex = useMemo(() => daysToDisplay[daysToDisplay.length - 1], [daysToDisplay]);
  const graphHeight = graphContainerRef?.current?.clientHeight ?? 0;
  const graphTop =
    (graphContainerRef?.current?.getBoundingClientRect()?.top ?? 0) +
    (getWindow()?.pageYOffset ?? 0) -
    120; // offset for header height

  const nextButtonDisabled = useMemo(
    () => lastDayIndex === forecastDaysAllowed - 1 || surf.loading || !!surf.error,
    [forecastDaysAllowed, lastDayIndex, surf.error, surf.loading],
  );
  const prevButtonDisabled = useMemo(
    () => firstDayIndex === 0 || surf.loading || !!surf.error,
    [firstDayIndex, surf.error, surf.loading],
  );

  return (
    <Box
      className={styles.forecastDataVisualizerContainer}
      data-testid="forecast-data-visualizer"
      ref={graphContainerRef}
    >
      <GraphHeader
        hasHistoricForecastPermissions={hasHistoricForecastPermissions}
        isHistoricView={isHistoricView}
        isTableViewMode={isTableViewMode}
        setStartDate={handleSetStartDate}
        spotId={spotId}
        spotName={spotName}
        startDate={startDate}
        subregionId={subregionId}
        subregionName={subregionOverview?.name}
        toggleHistoricView={toggleHistoricView}
        toggleTableView={toggleTableView}
      />
      <GraphDaySelectorContainer
        daysAllowed={forecastDaysAllowed}
        daysPerRange={daysPerRange}
        daySummary={daySummary}
        daysToDisplay={daysToDisplay}
        firstDayIndex={firstDayIndex}
        graphContainerRef={graphContainerRef}
        graphHeight={graphHeight}
        graphTop={graphTop}
        initialStartDayIndex={initialStartDayIndex}
        isHistoricView={showHistoricForecast}
        isPaywallActive={isPaywallActive}
        isTableViewMode={isTableViewMode}
        lastDayIndex={lastDayIndex}
        nextButtonDisabled={nextButtonDisabled}
        previousButtonDisabled={prevButtonDisabled}
        previousStartDay={previousStartDay}
        ratings={ratings}
        selectedStartDay={selectedStartDay}
        setPreviousStartDay={setPreviousStartDay}
        setSelectedStartDay={setSelectedStartDay}
        spotId={spotId}
        spotName={spotName}
        subregionOverview={subregionOverview}
        surf={surf}
        swipeLeftCount={swipeLeftCount}
        swipeRightCount={swipeRightCount}
        wind={wind}
      />
      {isTableViewMode && !didCoreFetchesError && (
        <Box>
          <ForecastTableV2
            daySummary={daySummary}
            daysAllowed={forecastDaysAllowed}
            hasHourlyDataAccess={hasHourlyDataAccess}
            isHistoricView={showHistoricForecast}
            ratings={ratings}
            consistency={consistency}
            sunlightTimes={sunlightTimes}
            tide={tide}
            trackingDetails={trackingDetails}
            utcOffset={utcOffset}
            surf={surf}
            swells={swells}
            waveEnergy={waveEnergy}
            weather={weatherRaw}
            wind={wind}
          />
          {surf?.runInitializationTimestamp && (
            <ModelRunTimestamp
              abbrTimezone={abbrTimezone}
              dateFormat={dateFormat}
              utcOffset={utcOffset}
              timestamp={surf?.runInitializationTimestamp}
            />
          )}
        </Box>
      )}
      {!isTableViewMode && !didCoreFetchesError && (
        <Box>
          <GraphContainer
            consistency={consistency}
            consistencyEnabled={consistencyEnabled}
            daySummary={daySummary}
            daysAllowed={forecastDaysAllowed}
            daysToDisplay={daysToDisplay}
            firstDayIndex={firstDayIndex}
            graphContainerRef={graphContainerRef}
            hasLiveWind={hasLiveWind}
            isHistoricView={showHistoricForecast}
            lastDayIndex={lastDayIndex}
            liveWind={liveWind}
            ratings={ratings}
            setSwipeLeftCount={setSwipeLeftCount}
            setSwipeRightCount={setSwipeRightCount}
            spectraDataAvailable={spectraDataAvailable}
            spotId={spotId}
            spotName={spotName}
            sunlightTimes={sunlightTimes}
            supportsInsights={supportsInsights}
            surf={surf}
            swellSpectra={swellSpectra}
            swells={swells}
            tide={tide}
            utcOffset={utcOffset}
            waveEnergy={waveEnergy}
            weather={weather}
            wind={wind}
          />
          {surf?.runInitializationTimestamp && !didCoreFetchesError && (
            <Box mt={4}>
              <ModelRunTimestamp
                abbrTimezone={abbrTimezone}
                dateFormat={dateFormat}
                utcOffset={utcOffset}
                timestamp={surf?.runInitializationTimestamp}
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

const ForecastDataVisualizer: React.FC<Props> = ({ ...restProps }) => (
  <ErrorBoundary
    errorBoundaryId="forecast-data-visualizer"
    render={() => (
      <Box className={styles.errorBoundaryContainer} data-testid="forecast-data-visualizer-error">
        <ErrorMessage />
      </Box>
    )}
  >
    <ForecastDataVisualizerContent {...restProps} />
  </ErrorBoundary>
);

export default ForecastDataVisualizer;
